import { ChevronDown, ExternalLink } from 'lucide-react';
import { useI18n } from '../../i18n/useI18n';
import type { CrawlUrlTreeNode } from './buildCrawlUrlTree';
import {
  countDescendantNodes,
  getNodeSelectionState,
} from './crawlUrlTreeHelpers';
import './CrawlUrlTree.css';

type CrawlUrlTreeRowProps = {
  node: CrawlUrlTreeNode;
  expandedNodeIds: Set<string>;
  onToggleExpanded: (nodeId: string) => void;
  /** When both are provided, checkboxes are shown for editable selection. */
  selectedNodeIds?: Set<string>;
  onToggleSelection?: (node: CrawlUrlTreeNode, selected: boolean) => void;
};

export function CrawlUrlTreeRow({
  node,
  expandedNodeIds,
  onToggleExpanded,
  selectedNodeIds,
  onToggleSelection,
}: CrawlUrlTreeRowProps) {
  const { translate } = useI18n();
  const hasChildren = node.children.length > 0;
  const isExpanded = hasChildren && expandedNodeIds.has(node.id);
  const descendantCount = hasChildren ? countDescendantNodes(node) : 0;
  const selectionEnabled =
    selectedNodeIds !== undefined && onToggleSelection !== undefined;
  const { checked, indeterminate } = selectionEnabled
    ? getNodeSelectionState(node, selectedNodeIds)
    : { checked: false, indeterminate: false };
  const label = node.url?.trim() || node.pathSegment?.trim() || '—';
  const openUrl =
    !node.isSynthetic && node.url?.trim() ? node.url.trim() : null;

  const urlLine = (
    <span className="crawl-url-tree__node-url-line">
      <span className="crawl-url-tree__node-url" title={label}>
        {label}
      </span>
      {openUrl ? (
        <a
          className="crawl-url-tree__node-open-link"
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={translate('knowledge.crawlSelectionOpenUrl', {
            url: openUrl,
          })}
          onMouseDown={
            selectionEnabled ? (event) => event.preventDefault() : undefined
          }
          onClick={
            selectionEnabled ? (event) => event.stopPropagation() : undefined
          }
        >
          <ExternalLink aria-hidden strokeWidth={2} />
        </a>
      ) : null}
    </span>
  );

  const countBadge =
    descendantCount > 0 ? (
      <span className="crawl-url-tree__node-count">{descendantCount}</span>
    ) : null;

  return (
    <li className="crawl-url-tree__node">
      <div className="crawl-url-tree__node-row">
        {hasChildren ? (
          <button
            type="button"
            className="crawl-url-tree__expand"
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? translate('knowledge.crawlSelectionCollapseNode')
                : translate('knowledge.crawlSelectionExpandNode')
            }
            onClick={() => onToggleExpanded(node.id)}
          >
            <ChevronDown
              className={[
                'crawl-url-tree__expand-icon',
                isExpanded && 'crawl-url-tree__expand-icon--expanded',
              ]
                .filter(Boolean)
                .join(' ')}
              strokeWidth={2}
              aria-hidden
            />
          </button>
        ) : (
          <span className="crawl-url-tree__expand-spacer" aria-hidden />
        )}
        {selectionEnabled ? (
          <label className="crawl-url-tree__node-label crawl-url-tree__node-label--selectable">
            <input
              type="checkbox"
              checked={checked}
              ref={(element) => {
                if (element) {
                  element.indeterminate = indeterminate;
                }
              }}
              onChange={(event) =>
                onToggleSelection(node, event.target.checked)
              }
            />
            {urlLine}
            {countBadge}
          </label>
        ) : (
          <span className="crawl-url-tree__node-label">
            {urlLine}
            {countBadge}
          </span>
        )}
      </div>
      {hasChildren && isExpanded ? (
        <ul className="crawl-url-tree__children">
          {node.children.map((child) => (
            <CrawlUrlTreeRow
              key={child.id}
              node={child}
              expandedNodeIds={expandedNodeIds}
              onToggleExpanded={onToggleExpanded}
              selectedNodeIds={selectedNodeIds}
              onToggleSelection={onToggleSelection}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
